import type { ColumnDef } from "@tanstack/react-table"

import { InventoryProductDescriptionCell, InventoryProductIdCell } from "../../../components"
import { ProductInventoryQtyOnHandCell, ProductInventoryRowActions, ProductInventoryWarehouseCell } from "../components"

import type { InventoryByProductItem } from "@/graphql/codegen/graphql"
import { formatNumber } from "@/utils/number"

interface UseProductInventoryColumnsProps {
  canMaintain: boolean
  onAdjust?: (inventory: InventoryByProductItem) => void
  onTransfer?: (inventory: InventoryByProductItem) => void
  onProductView?: (inventory: InventoryByProductItem) => void
  onCoaView?: (inventory: InventoryByProductItem) => void
}

export const useProductInventoryColumns = ({
  canMaintain,
  onAdjust,
  onTransfer,
  onProductView,
  onCoaView,
}: UseProductInventoryColumnsProps): ColumnDef<InventoryByProductItem>[] => {
  return [
    {
      accessorKey: "productId",
      header: "Product ID",
      cell: ({ row }) => <InventoryProductIdCell product={row.original.product} />,
      enableSorting: true,
    },
    {
      accessorKey: "description",
      header: "Description",
      cell: ({ row }) => <InventoryProductDescriptionCell product={row.original.product} />,
      enableSorting: true,
    },
    {
      accessorKey: "warehouses",
      header: "Warehouse",
      cell: ({ row }) => <ProductInventoryWarehouseCell inventories={row.original.warehouses} />,
      enableSorting: false,
    },
    {
      accessorKey: "qtyOnHand",
      header: "Qty on Hand",
      cell: ({ row }) => (
        <ProductInventoryQtyOnHandCell inventories={row.original.warehouses} qtyOnHand={row.original.qtyOnHand} />
      ),
      enableSorting: true,
    },
    {
      accessorKey: "availableQty",
      header: "Available",
      cell: ({ row }) => formatNumber(row.original.availableQty),
      enableSorting: true,
    },
    {
      accessorKey: "softAllocatedQty",
      header: "Soft Allocated",
      cell: ({ row }) => formatNumber(row.original.softAllocatedQty),
      enableSorting: true,
    },
    {
      accessorKey: "hardAllocatedQty",
      header: "Hard Allocated",
      cell: ({ row }) => formatNumber(row.original.hardAllocatedQty),
      enableSorting: true,
    },
    {
      accessorKey: "onHoldQty",
      header: "On Hold",
      cell: ({ row }) => formatNumber(row.original.onHoldQty),
      enableSorting: true,
    },
    {
      accessorKey: "balToCome",
      header: "Bal to Come",
      cell: ({ row }) => formatNumber(row.original.balToCome),
      enableSorting: true,
    },
    {
      accessorKey: "moh",
      header: "MOH",
      cell: ({ row }) => formatNumber(row.original.moh),
      enableSorting: true,
    },
    {
      accessorKey: "mohInStock",
      header: "MOH (In Stock)",
      cell: ({ row }) => formatNumber(row.original.mohInStock),
      enableSorting: true,
    },
    {
      accessorKey: "inventoryStatus",
      header: "Inv. Status",
      // Intentionally hidden due to https://robotsandpencils.atlassian.net/browse/AK-5741.
      // Inventory status column will be implemented in Build 11.
      cell: () => null, // <ProductInventoryStatusCell status={row.original.inventoryStatus} />,
      enableSorting: true,
    },
    {
      accessorKey: "actions",
      header: "",
      cell: ({ row }) => (
        <ProductInventoryRowActions
          row={row}
          canMaintain={canMaintain}
          onAdjust={onAdjust}
          onTransfer={onTransfer}
          onProductView={onProductView}
          onCoaView={onCoaView}
        />
      ),
      size: 50,
      enableSorting: false,
    },
  ]
}
